import { activityTrackerFor, createApiClient, type ApiClient } from '@brush/api-client';
import { ROOM_SCREENS, type RoomScreenName } from './activity/screens';
import { readActingRole, useAccessToken, useCurrentAccount, useSignOut } from '@brush/auth';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { apiBaseUrl, apiScope } from './authConfig';

const ApiContext = createContext<ApiClient | null>(null);

export interface Session {
  displayName: string | null;
  /** null when auth is disabled locally — there is nothing to sign out of. */
  signOut: (() => void) | null;
}

const SessionContext = createContext<Session>({ displayName: null, signOut: null });

export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) throw new Error('useApi must be used within an ApiProvider.');
  return client;
}

export function useSession(): Session {
  return useContext(SessionContext);
}

const DEV_SESSION: Session = { displayName: 'Development', signOut: null };

/** Local dev: no MSAL. The dev-bypass API authenticates every request. */
export function DevApiProvider({ children }: { children: ReactNode }) {
  const client = useMemo(
    () =>
      createApiClient({
        baseUrl: apiBaseUrl,
        getAccessToken: () => Promise.resolve('dev'),
        // Read from storage per request rather than captured here: switching role must not
        // require rebuilding the client, and a captured value would be stale the moment it did.
        getActingRole: readActingRole,
      }),
    [],
  );
  return (
    <ApiContext.Provider value={client}>
      <SessionContext.Provider value={DEV_SESSION}>{children}</SessionContext.Provider>
    </ApiContext.Provider>
  );
}

/** Production: real access token from MSAL. Must render inside <AuthProvider> and <RequireAuth>. */
export function MsalApiProvider({ children }: { children: ReactNode }) {
  const getAccessToken = useAccessToken(apiScope);
  const account = useCurrentAccount();
  const signOut = useSignOut();

  const client = useMemo(
    () => createApiClient({ baseUrl: apiBaseUrl, getAccessToken, getActingRole: readActingRole }),
    [getAccessToken],
  );
  const session = useMemo<Session>(
    () => ({ displayName: account?.name ?? null, signOut }),
    [account?.name, signOut],
  );

  return (
    <ApiContext.Provider value={client}>
      <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
    </ApiContext.Provider>
  );
}

/**
 * Reports this app's session once it is in use — once per app session, which the tracker keeps in
 * sessionStorage, so a reload is not a second session (ADR 0012 Decision 13). The console's own code: the
 * server refuses the other app's session from this app's token.
 */
export function useSessionStarted(ready: boolean) {
  const api = useApi();
  useEffect(() => {
    if (ready) activityTrackerFor(api).sessionStarted('console.session_started');
  }, [api, ready]);
}

/**
 * Reports a room-web screen on entry (ADR 0012 step 18) — once per screen per room per app session, which
 * the tracker keeps; a failure is never retried. Null reports nothing: the app is not in use yet (no room,
 * or the access check has not passed). The room is the one on screen, which the caller operates.
 */
export function useScreenViewed(screen: RoomScreenName | null, roomId: string | null) {
  const api = useApi();
  useEffect(() => {
    if (screen !== null) activityTrackerFor(api).viewed(ROOM_SCREENS[screen], roomId);
  }, [api, screen, roomId]);
}
